'use client'

import { SanityDocument } from 'next-sanity'
import { useOptimistic } from 'next-sanity/hooks'

import BlockRenderer from '@/app/components/BlockRenderer'
import { Button } from '@/app/components/Button'
import { TRACKING_LOCATIONS } from '@/app/utils/analytics'
import { GetPageQueryResult, HomeQueryResult } from '@/sanity.types'
import { dataAttr } from '@/sanity/lib/utils'

type PageBuilderPageProps = {
  page: GetPageQueryResult | HomeQueryResult
  headerTheme?: 'light' | 'dark' | null
}

type PageBuilderSection = {
  _key: string
  _type: string
}

type PageData = {
  _id: string
  _type: string
  pageBuilder?: PageBuilderSection[]
}

/**
 * The PageBuilder component is used to render the blocks from the `pageBuilder` field in the Page type in your Sanity Studio.
 */

function renderSections(
  pageBuilderSections: PageBuilderSection[],
  page: GetPageQueryResult | HomeQueryResult,
) {
  if (!page) {
    return null
  }
  return (
    <div
      data-sanity={dataAttr({
        id: page._id,
        type: page._type,
        path: `pageBuilder`,
      }).toString()}
      className="w-full overflow-x-clip"
    >
      {pageBuilderSections.map((block: any, index: number) => (
        <BlockRenderer
          key={block._key}
          index={index}
          block={block}
          pageId={page._id}
          pageType={page._type}
          isLast={index === pageBuilderSections.length - 1}
        />
      ))}
    </div>
  )
}

function renderEmptyState(page: GetPageQueryResult | HomeQueryResult) {
  if (!page) {
    return null
  }

  // Use absolute URL to ensure it opens in new tab
  const origin = typeof window !== 'undefined' ? window.location.origin : ''

  // Home documents use structure path, other documents use intent/edit
  let editUrl: string
  if (page._type === 'home') {
    // Extract language from document ID (e.g., "home-en" -> "en")
    const language = page._id.replace('home-', '')
    editUrl = `${origin}/studio/structure/language-${language};${language}-home`
  } else {
    editUrl = `${origin}/studio/structure/intent/edit/template=${page._type};type=${page._type};path=pageBuilder;id=${page._id}`
  }

  const addContentButton = {
    _type: 'button' as const,
    variant: 'primary' as const,
    size: 'md' as const,
    iconPlacement: 'none' as const,
    icon: null,
    dataId: null,
    link: {
      _type: 'link' as const,
      linkType: 'external' as const,
      external: editUrl,
      label: 'Add content to this page',
      openInNewTab: true,
      ariaLabel: null,
      mailto: null,
      tel: null,
      anchor: null,
      internalPage: null,
      internalPost: null,
      href: editUrl,
    },
  }

  return (
    <div className="content-container mt-5xl lg:mt-9xl">
      <div className="max-w-[1120px] mx-auto justify-center text-center">
        <h1 className="align-center">This page has no content!</h1>
        <p className="mt-4xl">Open the page in Sanity Studio to add content.</p>
        <div className="mt-6xl">
          <Button
            button={addContentButton}
            tracking={{ location: TRACKING_LOCATIONS.PAGE_CONTENT }}
          />
        </div>
      </div>
    </div>
  )
}

export const PageBuilder = function ({ page }: PageBuilderPageProps) {
  const pageBuilderSections = useOptimistic<
    PageBuilderSection[] | undefined,
    SanityDocument<PageData>
  >(page?.pageBuilder || [], (currentSections, action) => {
    // The action contains updated document data from Sanity
    // when someone makes an edit in the Studio

    // If the edit was to a different document, ignore it
    if (action.id !== page?._id) {
      return currentSections
    }

    // If there are sections in the updated document, use them
    if (action.document.pageBuilder) {
      // Reconcile References. https://www.sanity.io/docs/enabling-drag-and-drop#ffe728eea8c1
      return action.document.pageBuilder.map(
        (section) => currentSections?.find((s) => s._key === section?._key) || section,
      )
    }

    // Otherwise keep the current sections
    return currentSections
  })

  if (!page) {
    return renderEmptyState(page)
  }

  return pageBuilderSections && pageBuilderSections.length > 0
    ? renderSections(pageBuilderSections, page)
    : renderEmptyState(page)
}
